import type { ReactNode } from "react";
import { css } from "styled-system/css";
import type { DeliveryMeta } from "@aura/shared";
import { Masthead, btn, ed, field } from "~/components/editorial";
import { plural } from "~/lib/format";
import { DELIVERY_LINKS, freshness } from "./lib";

export function ReportShell({ meta, children }: { meta?: string; children: ReactNode }) {
  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Masthead links={DELIVERY_LINKS} meta={meta} label="Delivery reports" />
        {children}
      </div>
    </div>
  );
}

export function BotsToggle({
  includeBots,
  onChange,
}: {
  includeBots: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={btn.outline}
      aria-pressed={includeBots}
      onClick={() => onChange(!includeBots)}
    >
      {includeBots ? "Bots included" : "Bots excluded"}
    </button>
  );
}

/** Uppercase segmented control: Month | Quarter, Week | Month | Quarter. */
export function Segments<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className={styles.seg}>
      <span className={styles.segLabel}>{label}</span>
      <div className={field.segs} role="group" aria-label={label}>
        {options.map((o) => (
          <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Controls({ children }: { children: ReactNode }) {
  return <div className={styles.controls}>{children}</div>;
}

/** Byline: which orgs, how fresh, bots in or out. */
export function Byline({ title, meta, extra }: { title: string; meta: DeliveryMeta; extra?: string }) {
  return (
    <div className={ed.byline}>
      <b>{title}</b> · merged PRs in {plural(meta.orgs.length, "GitHub org")} (
      {meta.orgs.map((o) => o.name).join(", ")}). {freshness(meta)}.{" "}
      {meta.includeBots ? "Bot-authored PRs included." : "Bot-authored PRs left out."}
      {extra ? ` ${extra}` : ""}
    </div>
  );
}

function CsvLink({ href }: { href: string }) {
  return (
    <a className={btn.sentenceOutline} href={href} download>
      Download CSV
    </a>
  );
}

export function SectionHead({ title, href }: { title: string; href?: string }) {
  return (
    <div className={styles.head}>
      <h2 className={ed.h2}>{title}</h2>
      {href && <CsvLink href={href} />}
    </div>
  );
}

const styles = {
  head: css({
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  }),
  controls: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "12px 28px",
    py: "10px",
    mb: "8px",
  }),
  seg: css({ display: "flex", alignItems: "center", gap: "10px" }),
  segLabel: css({
    fontFamily: "sans",
    fontSize: "11px",
    color: "ed.ink3",
    letterSpacing: "0.04em",
  }),
};
